import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import Cookies from 'js-cookie';

export const UserContext = createContext();

export const useUser = () => {
    const context = useContext(UserContext);
    
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    
    return context;
};

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isLoadingUser, setIsLoadingUser] = useState(true); // New loading state

    // Simple logging function
    const log = (message, data = {}) => {
        console.log(`[UserContext] ${message}:`, data, new Date().toISOString());
    };

    // Load user from cookie on initial mount
    useEffect(() => {
        const userCookie = Cookies.get('user');
        const tokenCookie = Cookies.get('token');
        
        if (userCookie && tokenCookie) {
            try {
                const userData = JSON.parse(userCookie);
                if (userData && userData.id) {
                    setUser(userData);
                    log('Loaded user from cookies', { id: userData.id });
                }
            } catch (error) {
                console.error('Error parsing user cookie:', error);
                Cookies.remove('user', { path: '/' });
                Cookies.remove('token', { path: '/' });
            } finally {
                setIsLoadingUser(false); // Set loading to false after attempting to load
            }
        } else {
            setIsLoadingUser(false); // Also set to false if no cookies found
        }
    }, []);
    
    // Simple cookie-state synchronization 
    useEffect(() => {
        // Create synchronization function outside the interval
        const syncCookiesAndState = () => {
            const userCookie = Cookies.get('user');
            const tokenCookie = Cookies.get('token');
            
            // If we have cookies but no user state, restore from cookies
            if (userCookie && tokenCookie && !user) {
                try {
                    const userData = JSON.parse(userCookie);
                    if (userData && userData.id) {
                        setUser(userData);
                    }
                } catch (error) {
                    // Invalid cookie data - clear them
                    Cookies.remove('user', { path: '/' });
                    Cookies.remove('token', { path: '/' });
                }
            }
            
            // If we have user state but no cookies, clear the state
            if ((!userCookie || !tokenCookie) && user) {
                setUser(null);
            }
        };
        
        // Run once and set up interval
        syncCookiesAndState();
        const interval = setInterval(syncCookiesAndState, 2000);
        
        return () => clearInterval(interval);
    }, [user]);

    // Refresh user data from server
    const refreshUserData = useCallback(async () => {
        if (!user?.id) return;
        
        try {
            setIsRefreshing(true);
            log('Refreshing user data', { id: user.id });
            
            const response = await fetch(`https://service3.szapfs.org/cliniciandetails/${user.id}`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch user: ${response.status}`);
            }
            
            const userData = await response.json();
            setUser(userData);
            Cookies.set('user', JSON.stringify(userData), { path: '/', expires: 180 });
        } catch (error) {
            console.error('Error refreshing user data:', error);
        } finally {
            setIsRefreshing(false);
        }
    }, [user]);

    // Update user data
    const updateUser = useCallback((userData) => {
        log('Updating user', { id: userData?.id });
        setUser(userData);
        Cookies.set('user', JSON.stringify(userData), { path: '/', expires: 180 });
    }, []);

    // Clear user data
    const clearUser = useCallback(() => {
        log('Clearing user');
        setUser(null);
        
        // Clear cookies
        Cookies.remove('user', { path: '/' });
        Cookies.remove('token', { path: '/' });
        
        // Extra fallback for stubborn cookies
        document.cookie = 'user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }, []);

    return (
        <UserContext.Provider value={{ 
            user, 
            updateUser, 
            clearUser, 
            refreshUserData,
            isRefreshing,
            isLoadingUser // Expose isLoadingUser
        }}>
            {children}
        </UserContext.Provider>
    );
};
